import { useState, useMemo, useEffect } from "react";
import { can, FLOORS, STATUSES_M, RETIRED_STATUSES, machineTypeKey, checkSheetTypeOptions, checkSheetsForName, noScheduleReason } from "../config";
import { apiCall } from "../api";
import { Input, Select, Btn } from "./common/FormElements";
import { Modal } from "./common/Modal";
import { RevisionHistory } from "./RevisionHistory";
import html2pdf from "html2pdf.js";

function escapeHtml(str) {
  if (str == null) return "";
  return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
}

const BLANK_FILTERS = { tab: "machines", search: "", dept: "ALL", type: "ALL", sortBy: "default", schedule: "ALL" };

import { listDrafts, formatDraftStamp, draftWho } from "../drafts";

export default function MachineListPage({ machines, setMachines, user, onSelect, unitId, refreshMachines, refreshPM, departments, floors, units, filters, onFiltersChange, pmCheckSheetTemplates = [], pmPlan = [] }) {
  // The tab and the search / department / sort filters are held by the parent so they
  // survive opening a machine's History Card and coming back — this page unmounts while
  // the History Card is shown. Falls back to local state if used without those props.
  const [localFilters, setLocalFilters] = useState(BLANK_FILTERS);
  const f = filters || localFilters;
  const setF = onFiltersChange || setLocalFilters;
  const activeTab = f.tab || "machines";
  const setActiveTab = tab => setF(prev => ({ ...prev, tab }));
  return (
    <div className="space-y-4" data-testid="machine-list-page">
      <div className="flex gap-2 border-b border-gray-200">
        {[{ id: "machines", label: "Machine List" }, { id: "revisions", label: "Revision History" }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`} data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>
      {activeTab === "machines" && <MachineList machines={machines} setMachines={setMachines} user={user} onSelect={onSelect} unitId={unitId} refreshMachines={refreshMachines} refreshPM={refreshPM} departments={departments} floors={floors} units={units || []} filters={f} setFilters={setF} pmCheckSheetTemplates={pmCheckSheetTemplates} pmPlan={pmPlan} />}
      {activeTab === "revisions" && <RevisionHistory user={user} unitId={unitId} />}
    </div>
  );
}

function MachineList({ machines, setMachines, user, onSelect, unitId, refreshMachines, refreshPM, departments, floors, units, filters, setFilters, pmCheckSheetTemplates = [], pmPlan = [] }) {
  // Every draft in progress in this unit, whoever started it, so an unfinished log can
  // be found from the list instead of only by reopening the machine it was typed on.
  const [myDrafts, setMyDrafts] = useState({});
  useEffect(() => {
    let cancelled = false;
    listDrafts(unitId).then(list => {
      if (cancelled) return;
      const byMachine = {};
      for (const d of list) byMachine[d.machineId] = d;
      setMyDrafts(byMachine);
    });
    return () => { cancelled = true; };
  }, [unitId]);
  const draftCount = Object.keys(myDrafts).length;

  // Search / department / sort are owned by the parent so they are still there when you
  // come back from a machine's History Card.
  const search = filters?.search ?? "";
  const dept = filters?.dept ?? "ALL";
  const machineType = filters?.type ?? "ALL";
  const sortBy = filters?.sortBy ?? "default";
  const scheduleFilter = filters?.schedule ?? "ALL";
  const setSearch = v => setFilters(prev => ({ ...prev, search: v }));
  const setDept = v => setFilters(prev => ({ ...prev, dept: v }));
  const setType = v => setFilters(prev => ({ ...prev, type: v }));
  const setSortBy = v => setFilters(prev => ({ ...prev, sortBy: v }));
  const setScheduleFilter = v => setFilters(prev => ({ ...prev, schedule: v }));
  const [editMachine, setEditMachine] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [transferMachine, setTransferMachine] = useState(null);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const deptOptions = useMemo(() => ["ALL", ...(departments || []).map(d => d.name)], [departments]);
  // Machine types come from the machines themselves rather than a fixed list: the name
  // IS the type here ("PNEUMATIC PRESS", "CNC", "DRILL MACHINE"), and a hardcoded list
  // would fall behind the moment a new kind of machine is added. Counted so the size of
  // each group is visible before picking it.
  const typeOptions = useMemo(() => {
    const counts = new Map();
    for (const m of machines || []) {
      const t = (m.name || "").trim().toUpperCase();
      if (t) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return [{ value: "ALL", label: `All types (${(machines || []).length})` },
            ...[...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]))
              .map(([t, n]) => ({ value: t, label: `${t} (${n})` }))];
  }, [machines]);
  // Floors are configured per unit in Settings; fall back to the built-in list if a unit
  // has none yet, so the form is never left with an empty dropdown.
  const floorOptions = useMemo(() => {
    const names = (floors || []).map(f => f.name).filter(Boolean);
    return names.length ? names : FLOORS;
  }, [floors]);
  // A machine that has been sold or scrapped is off the floor, so it is out of the
  // working list by default. It is not deleted: Reports -> Machine Movements lists it,
  // its history stays, and "Show sold / scrapped" brings it back into view here.
  const isRetired = m => RETIRED_STATUSES.includes(String(m.status || "").trim());
  const retiredCount = useMemo(() => (machines || []).filter(isRetired).length, [machines]);

  // Why each machine is getting no PM this year, keyed by id — the PM Plan says this on
  // its own screen, but the master list is where you look when deciding what to do about
  // a machine, so it has to say it here too.
  const noSchedule = useMemo(() => {
    const out = {};
    for (const m of machines || []) {
      const why = noScheduleReason(m, pmPlan);
      if (why) out[m.id] = why;
    }
    return out;
  }, [machines, pmPlan]);
  const noScheduleCount = useMemo(
    () => (machines || []).filter(m => noSchedule[m.id] && !isRetired(m)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [machines, noSchedule]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (machines || []).filter(m =>
      (showRetired || !isRetired(m))
      && (dept === "ALL" || m.dept === dept)
      && (machineType === "ALL" || (m.name || "").trim().toUpperCase() === machineType)
      && (scheduleFilter === "ALL"
          || (scheduleFilter === "NONE" ? !!noSchedule[m.id] : !noSchedule[m.id]))
      && (!q || m.name.toLowerCase().includes(q) || m.machineNo.toLowerCase().includes(q) || m.make.toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machines, search, dept, machineType, showRetired, scheduleFilter, noSchedule]);
  const grouped = useMemo(() => filtered.reduce((a, m) => { (a[m.dept] = a[m.dept] || []).push(m); return a; }, {}), [filtered]);

  // Clicking a heading sorts by it; clicking again reverses. Kept in the same sortBy
  // state the Sort dropdown uses, so the two controls always agree rather than quietly
  // overriding each other.
  const COLUMN_SORTS = {
    name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    machineNo: (a, b) => (a.machineNo || "").localeCompare(b.machineNo || "", undefined, { numeric: true }),
    capacity: (a, b) => {
      const tons = m => { const n = (m.capacity || "").match(/[\d.]+/); return n ? parseFloat(n[0]) : 0; };
      return tons(a) - tons(b);
    },
    make: (a, b) => (a.make || "").localeCompare(b.make || ""),
    year: (a, b) => (a.year || 0) - (b.year || 0),
    dept: (a, b) => (a.dept || "").localeCompare(b.dept || ""),
    floor: (a, b) => (a.floor || "").localeCompare(b.floor || ""),
    status: (a, b) => (a.status || "Active").localeCompare(b.status || "Active"),
  };

  function toggleSort(key) {
    setSortBy(sortBy === `${key}_asc` ? `${key}_desc` : `${key}_asc`);
  }

  function sortArrow(key) {
    if (sortBy === `${key}_asc`) return "\u25B2";
    if (sortBy === `${key}_desc`) return "\u25BC";
    return "\u21C5";          // both ways, so a sortable column says so before it is used
  }

  function sortMachines(list) {
    if (sortBy === "default") return list;
    const [key, dir] = String(sortBy).split("_");
    const compare = COLUMN_SORTS[key];
    if (compare) return [...list].sort((a, b) => (dir === "desc" ? -compare(a, b) : compare(a, b)));
    return [...list].sort((a, b) => {
      if (sortBy === "capacity_asc" || sortBy === "capacity_desc") {
        const getTonnage = (m) => { const match = (m.capacity || "").match(/[\d.]+/); return match ? parseFloat(match[0]) : 0; };
        return sortBy === "capacity_asc" ? getTonnage(a) - getTonnage(b) : getTonnage(b) - getTonnage(a);
      }
      if (sortBy === "make_asc") return (a.make || "").localeCompare(b.make || "");
      if (sortBy === "make_desc") return (b.make || "").localeCompare(a.make || "");
      if (sortBy === "year_asc") return (a.year || 0) - (b.year || 0);
      if (sortBy === "year_desc") return (b.year || 0) - (a.year || 0);
      if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
      return 0;
    });
  }

  const otherUnits = useMemo(() => units.filter(u => u.id !== unitId), [units, unitId]);
  const currentUnitName = units.find(u => u.id === unitId)?.shortName || unitId;

  const blankForm = { id: `${unitId}-${Date.now()}`, dept: "", name: "", capacity: "", machineNo: "", make: "", year: new Date().getFullYear(), floor: "GROUND FLOOR", status: "Active", machineModel: "", motorCapacity: "", maintenanceFrequency: "", commissioningDate: "", manualAvailability: "", poNumber: "", slideLength: "", slideWidth: "", bedLength: "", bedWidth: "", bedHeight: "" };

  function startEdit(m) { setForm({ ...m }); setEditMachine(m.id); setShowAdd(false); }
  function startAdd() { setForm({ ...blankForm }); setShowAdd(true); setEditMachine(null); }

  async function saveEdit() {
    setSaving(true);
    try { await apiCall("PUT", `/machines/${unitId}/${form.id}`, { ...form, year: Number(form.year) }); await refreshMachines(); setEditMachine(null); }
    catch (error) { alert("Failed to save machine"); }
    finally { setSaving(false); }
  }

  async function saveAdd() {
    setSaving(true);
    try { await apiCall("POST", `/machines/${unitId}`, { ...form, year: Number(form.year) }); await refreshMachines(); if (refreshPM) await refreshPM(); setShowAdd(false); }
    catch (error) { alert("Failed to add machine"); }
    finally { setSaving(false); }
  }

  async function deleteMachine(id) {
    if (window.confirm("Delete this machine?")) {
      try { await apiCall("DELETE", `/machines/${unitId}/${id}`); await refreshMachines(); if (refreshPM) await refreshPM(); }
      catch (error) { alert("Failed to delete machine"); }
    }
  }

  async function downloadQR(m) {
    try {
      const baseUrl = window.location.origin;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/machines/${unitId}/${m.id}/qr-badge?base_url=${encodeURIComponent(baseUrl)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) throw new Error("Failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `QR_${m.machineNo}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Failed to download QR badge");
    }
  }

  async function doTransfer() {
    if (!transferMachine || !transferTarget) return;
    const targetName = units.find(u => u.id === transferTarget)?.shortName || transferTarget;
    if (!window.confirm(`Transfer ${transferMachine.name} from ${currentUnitName} to ${targetName}? This will move the machine and all its data.`)) return;
    setTransferring(true);
    try {
      await apiCall("POST", `/machines/${unitId}/${transferMachine.id}/transfer`, { target_unit_id: transferTarget });
      alert(`${transferMachine.name} transferred to ${targetName} successfully.`);
      setTransferMachine(null);
      setTransferTarget("");
      await refreshMachines();
    } catch (error) {
      alert(error.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const revisions = await apiCall("GET", `/machine-revisions/${unitId}`);
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2,"0")}-${String(now.getMonth()+1).padStart(2,"0")}-${now.getFullYear()}`;
      const allGrouped = machines.reduce((a, m) => { (a[m.dept] = a[m.dept] || []).push(m); return a; }, {});

      const statusColor = (s) => s === "Active" ? "color:#16a34a" : s === "Under Maintenance" ? "color:#d97706" : "color:#6b7280";
      const th = 'style="border:1px solid #ddd;padding:4px 8px;font-size:10px;background:#f8f8f8;font-weight:bold;text-align:left"';
      const td = 'style="border:1px solid #ddd;padding:4px 8px;font-size:10px"';

      let html = `<div style="font-family:Arial,sans-serif">`;
      html += `<div style="font-size:16px;font-weight:bold;margin-bottom:4px">Machine List — ${escapeHtml(currentUnitName)}</div>`;
      html += `<div style="font-size:11px;color:#666;margin-bottom:10px">Master List: MNT-F-01 | Generated: ${dateStr} | Total: ${machines.length} machines</div>`;

      for (const [deptName, mList] of Object.entries(allGrouped)) {
        html += `<div style="background:#f0f0f0;font-weight:bold;font-size:12px;padding:6px 8px;margin-top:14px;border:1px solid #ddd">${escapeHtml(deptName)} (${mList.length})</div>`;
        html += `<table style="border-collapse:collapse;width:100%"><thead><tr>`;
        for (const h of ["#","Machine Name","Machine No.","Capacity","Make","Year","Floor","Status"]) html += `<th ${th}>${h}</th>`;
        html += `</tr></thead><tbody>`;
        mList.forEach((m, i) => {
          html += `<tr>`;
          html += `<td ${td}>${i+1}</td>`;
          html += `<td ${td}>${escapeHtml(m.name)}</td>`;
          html += `<td ${td}>${escapeHtml(m.machineNo)}</td>`;
          html += `<td ${td}>${escapeHtml(m.capacity)}</td>`;
          html += `<td ${td}>${escapeHtml(m.make)}</td>`;
          html += `<td ${td}>${m.year}</td>`;
          html += `<td ${td}>${escapeHtml(m.floor)}</td>`;
          html += `<td ${td} style="border:1px solid #ddd;padding:4px 8px;font-size:10px;${statusColor(m.status)};font-weight:600">${escapeHtml(m.status)}</td>`;
          html += `</tr>`;
        });
        html += `</tbody></table>`;
      }

      html += `<div style="font-size:14px;font-weight:bold;margin-top:24px;margin-bottom:8px">Revision History</div>`;
      const sortedRevs = [...revisions].sort((a, b) => (b.revNo || 0) - (a.revNo || 0));
      html += `<table style="border-collapse:collapse;width:100%"><thead><tr>`;
      for (const h of ["Rev No.","Rev Date","Revision Details","Revised By","Approved By"]) html += `<th ${th}>${h}</th>`;
      html += `</tr></thead><tbody>`;
      sortedRevs.forEach(r => {
        html += `<tr>`;
        html += `<td ${td}>${r.revNo ?? ""}</td>`;
        html += `<td ${td}>${escapeHtml(r.revDate)}</td>`;
        html += `<td ${td} style="border:1px solid #ddd;padding:4px 8px;font-size:9px">${escapeHtml(r.details)}</td>`;
        html += `<td ${td}>${escapeHtml(r.revisedBy)}</td>`;
        html += `<td ${td}>${escapeHtml(r.approvedBy)}</td>`;
        html += `</tr>`;
      });
      html += `</tbody></table></div>`;

      const div = document.createElement("div");
      div.innerHTML = html;
      document.body.appendChild(div);

      const opt = {
        margin: 5,
        filename: `Machine_List_${unitId}_${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      await html2pdf().set(opt).from(div).save();
      document.body.removeChild(div);
    } catch (error) {
      alert("PDF export failed: " + (error.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }

  async function handleExportPressGroups() {
    setExporting(true);
    try {
      const data = await apiCall("GET", `/machines/${unitId}/export-press-groups`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `press-groups-${unitId}-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Export failed: " + (error.response?.data?.detail || error.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }

  // The PM plan finds a check sheet by matching the machine's NAME against a template's
  // machine type, so a name typed even slightly differently gets no sheet at all — which
  // is how "checksheet not show here" happens. Offer the published names as a dropdown,
  // each showing the documents behind it, and say plainly whether the typed name matches.
  const checkSheetTypes = useMemo(() => checkSheetTypeOptions(pmCheckSheetTemplates), [pmCheckSheetTemplates]);
  const nameSheets = useMemo(() => checkSheetsForName(pmCheckSheetTemplates, form.name), [pmCheckSheetTemplates, form.name]);

  const MachineForm = ({ onSave, onCancel, title }) => (
    <Modal title={title} onClose={onCancel}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input label="Machine Name" value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            list="machine-checksheet-types" testId="machine-name-input"
            placeholder={checkSheetTypes.length ? "Pick a check sheet name, or type your own" : ""} />
          {/* A plain list, not a fixed select: a machine with no published sheet still has
              to be addable, and the existing names are not all check sheet types. */}
          <datalist id="machine-checksheet-types">
            {checkSheetTypes.map(o => <option key={o.machineType} value={o.machineType}>{o.label}</option>)}
          </datalist>
          {form.name ? (
            nameSheets.length ? (
              <p className="text-xs text-green-700 mt-1" data-testid="machine-name-checksheet">
                &#10003; Check sheet: {nameSheets.map(sh => `${sh.docNo || "\u2014"} ${sh.frequency}`).join(" / ")}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-1" data-testid="machine-name-checksheet">
                No check sheet for this name &mdash; pick one from the list if this machine should have one.
              </p>
            )
          ) : null}
        </div>
        <Input label="Machine No. / ID" value={form.machineNo || ""} onChange={e => setForm(f => ({ ...f, machineNo: e.target.value }))} />
        <Input label="Machine Model" value={form.machineModel || ""} onChange={e => setForm(f => ({ ...f, machineModel: e.target.value }))} />
        <Select label="Department / Section" value={form.dept || ""} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))} options={[{ value: "", label: "-- Select Department --" }, ...departments.map(d => ({ value: d.name, label: d.name }))]} />
        <Select label="Floor / Location" value={form.floor || ""} onChange={e => setForm(f => ({ ...f, floor: e.target.value }))} options={floorOptions} />
        <Input label="Machine Capacity" value={form.capacity || ""} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
        <Input label="Motor Capacity" value={form.motorCapacity || ""} onChange={e => setForm(f => ({ ...f, motorCapacity: e.target.value }))} />
        <Input label="Make / Manufacturer" value={form.make || ""} onChange={e => setForm(f => ({ ...f, make: e.target.value }))} />
        <Input label="Year of Purchase" value={form.year || ""} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} type="number" />
        <Input label="Commissioning Date" value={form.commissioningDate || ""} onChange={e => setForm(f => ({ ...f, commissioningDate: e.target.value }))} placeholder="e.g. 01/2012" />
        <Input label="Maintenance Frequency" value={form.maintenanceFrequency || ""} onChange={e => setForm(f => ({ ...f, maintenanceFrequency: e.target.value }))} placeholder="e.g. 6 MONTH" />
        <Input label="Manual Availability" value={form.manualAvailability || ""} onChange={e => setForm(f => ({ ...f, manualAvailability: e.target.value }))} placeholder="Available / Not Available" />
        <Input label="PO Number" value={form.poNumber || ""} onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} placeholder="e.g. PO/2024/0123" />
      </div>
      {/* Bed size as three measurements rather than one typed string, so it can be
          searched and compared, and so the ones still missing can be counted while it
          is being filled in. Millimetres, like the plant's drawings. */}
      {/* Slide size above bed size, as they are read on the machine. Two measurements,
          not three — a slide has no height. */}
      <div className="border-t pt-3">
        <label className="text-xs text-gray-500 font-medium block mb-1">Slide Size (mm)</label>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Length" value={form.slideLength || ""} onChange={e => setForm(f => ({ ...f, slideLength: e.target.value }))} placeholder="e.g. 600" />
          <Input label="Width" value={form.slideWidth || ""} onChange={e => setForm(f => ({ ...f, slideWidth: e.target.value }))} placeholder="e.g. 400" />
        </div>
      </div>
      <div className="border-t pt-3">
        <label className="text-xs text-gray-500 font-medium block mb-1">Bed Size (mm)</label>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Length" value={form.bedLength || ""} onChange={e => setForm(f => ({ ...f, bedLength: e.target.value }))} placeholder="e.g. 1000" />
          <Input label="Width" value={form.bedWidth || ""} onChange={e => setForm(f => ({ ...f, bedWidth: e.target.value }))} placeholder="e.g. 500" />
          <Input label="Height" value={form.bedHeight || ""} onChange={e => setForm(f => ({ ...f, bedHeight: e.target.value }))} placeholder="e.g. 300" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Status" value={form.status || "Active"} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} options={STATUSES_M} />
      </div>
      <div className="flex gap-2 mt-4">
        <Btn onClick={onSave} disabled={saving} testId="save-machine-btn">{saving ? "Saving..." : "Save Changes"}</Btn>
        <Btn onClick={onCancel} variant="ghost">Cancel</Btn>
      </div>
    </Modal>
  );

  return (
    <div className="space-y-4" data-testid="machine-list">
      {editMachine && MachineForm({ title: "Edit Machine", onSave: saveEdit, onCancel: () => setEditMachine(null) })}
      {showAdd && MachineForm({ title: "Add New Machine", onSave: saveAdd, onCancel: () => setShowAdd(false) })}
      {transferMachine && (
        <Modal title="Transfer Machine" onClose={() => { setTransferMachine(null); setTransferTarget(""); }}>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 font-medium">Machine</label>
              <div className="mt-0.5 px-3 py-2 bg-gray-50 border rounded-lg text-sm font-medium text-gray-800" data-testid="transfer-machine-name">
                {transferMachine.name} <span className="text-xs font-mono text-gray-500 ml-1">({transferMachine.machineNo})</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">From</label>
              <div className="mt-0.5 px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600">{currentUnitName}</div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-0.5">Transfer to</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={transferTarget}
                onChange={e => setTransferTarget(e.target.value)}
                data-testid="transfer-unit-select"
              >
                <option value="">-- Select target unit --</option>
                {otherUnits.map(u => <option key={u.id} value={u.id}>{u.shortName || u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Btn onClick={doTransfer} disabled={transferring || !transferTarget} testId="confirm-transfer-btn">
              {transferring ? "Transferring..." : "Transfer"}
            </Btn>
            <Btn onClick={() => { setTransferMachine(null); setTransferTarget(""); }} variant="ghost">Cancel</Btn>
          </div>
        </Modal>
      )}
      <div className="flex flex-col sm:flex-row gap-2 justify-between">
        <div className="flex gap-2 flex-1">
          <input className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="Search by name, number, make..." value={search} onChange={e => setSearch(e.target.value)} data-testid="machine-search" />
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={dept} onChange={e => setDept(e.target.value)} data-testid="dept-filter">
            {deptOptions.map(d => <option key={d}>{d}</option>)}
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={machineType} onChange={e => setType(e.target.value)} data-testid="type-filter" title="Show only one kind of machine">
            {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={scheduleFilter}
            onChange={e => setScheduleFilter(e.target.value)} data-testid="schedule-filter"
            title="Machines getting no preventive maintenance this financial year">
            <option value="ALL">PM: All</option>
            <option value="NONE">No schedule ({noScheduleCount})</option>
            <option value="SOME">Scheduled only</option>
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="sort-select">
            <option value="default">Sort: Default</option>
            <option value="capacity_desc">Capacity: High to Low</option>
            <option value="capacity_asc">Capacity: Low to High</option>
            <option value="make_asc">Make: A to Z</option>
            <option value="make_desc">Make: Z to A</option>
            <option value="year_asc">Year: Oldest First</option>
            <option value="year_desc">Year: Newest First</option>
            <option value="name_asc">Name: A to Z</option>
          </select>
        </div>
        <div className="flex gap-2">
          {user?.role === "superadmin" && (
            <button onClick={handleExportPressGroups} disabled={exporting} className="border border-purple-600 text-purple-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-50 disabled:opacity-50" data-testid="export-press-groups-btn">
              {exporting ? "Exporting..." : "Export Press Groups"}
            </button>
          )}
          {can(user, "editMachines") && (
            <button onClick={handleExportPDF} disabled={exporting} className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 disabled:opacity-50" data-testid="export-pdf-btn">
              {exporting ? "Exporting..." : "Export PDF"}
            </button>
          )}
          {can(user, "addMachines") && <Btn onClick={startAdd} testId="add-machine-btn">+ Add Machine</Btn>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-400">{filtered.length} machines &middot; Master List: MNT-F-01 &middot; Updated April &amp; October</div>
        {retiredCount > 0 && (
          <button onClick={() => setShowRetired(v => !v)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
            data-testid="toggle-retired">
            {showRetired ? `Hide ${retiredCount} sold / scrapped` : `Show ${retiredCount} sold / scrapped`}
          </button>
        )}
        {draftCount > 0 && (
          <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
            title="Maintenance logs you started but have not submitted. Open the machine and press Add Maintenance Log to continue."
            data-testid="draft-summary">
            {draftCount} unfinished {draftCount === 1 ? "draft" : "drafts"} &middot; marked DRAFT below
          </span>
        )}
      </div>
      {Object.entries(grouped).map(([deptName, mList]) => (
        <div key={deptName} className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex justify-between">
            <h3 className="font-semibold text-gray-700 text-sm">{deptName}</h3>
            <span className="text-xs text-gray-400">{mList.length} machines</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-700 to-blue-500 text-white">
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide">#</th>
                  {[["name", "Machine Name"], ["machineNo", "Machine No."], ["capacity", "Capacity"],
                    ["make", "Make"], ["year", "Year"], ["dept", "Department"], ["floor", "Floor"],
                    ["status", "Status"]].map(([key, label]) => (
                    <th key={key} onClick={() => toggleSort(key)}
                      className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide cursor-pointer select-none hover:bg-white/10 whitespace-nowrap"
                      title={`Sort by ${label}`} data-testid={`sort-${key}`}>
                      {label}
                      <span className="ml-1 opacity-70">{sortArrow(key)}</span>
                    </th>
                  ))}
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {sortMachines(mList).map((m, i) => (
                  <tr key={m.id} className="border-b hover:bg-blue-50 transition-colors">
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 cursor-pointer hover:text-blue-600" onClick={() => onSelect(m)} data-testid={`machine-name-${m.id}`}>
                      {m.name}
                      {isRetired(m) && (
                        <span className={`ml-2 align-middle inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold ${String(m.status).trim() === "Sold Out" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}
                          title={`${m.status} — kept for the record, no PM expected`}
                          data-testid={`retired-badge-${m.id}`}>{String(m.status).toUpperCase()}</span>
                      )}
                      {/* Not shown on a retired machine — no PM is expected once it has
                          left the floor, so the RETIRED badge already says it. */}
                      {noSchedule[m.id] && !isRetired(m) && (
                        <span className="ml-2 align-middle inline-block rounded-full bg-orange-100 text-orange-700 px-1.5 py-0.5 text-[10px] font-bold"
                          title={`Getting no preventive maintenance — ${noSchedule[m.id]}`}
                          data-testid={`no-schedule-badge-${m.id}`}>NO SCHEDULE</span>
                      )}
                      {myDrafts[m.id] && (
                        <span className="ml-2 align-middle inline-block rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold"
                          title={`Shared draft — ${draftWho(myDrafts[m.id])} · last saved ${formatDraftStamp(myDrafts[m.id].updatedAt)}. Open the machine to continue it.`}
                          data-testid={`draft-badge-${m.id}`}>DRAFT</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-blue-600 text-xs">{m.machineNo}</td>
                    <td className="px-3 py-2 text-gray-600">{m.capacity}</td>
                    <td className="px-3 py-2 text-gray-600">{m.make}</td>
                    <td className="px-3 py-2 text-gray-600">{m.year}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{m.dept}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{m.floor}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${m.status === "Active" ? "bg-green-100 text-green-700" : m.status === "Under Maintenance" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{m.status}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => onSelect(m)} className="text-blue-400 hover:text-blue-600 text-xs" data-testid={`history-btn-${m.id}`}>History</button>
                        <button onClick={() => downloadQR(m)} className="text-purple-400 hover:text-purple-600 text-xs ml-1" data-testid={`qr-btn-${m.id}`}>QR</button>
                        {can(user, "editMachines") && <button onClick={() => startEdit(m)} className="text-gray-400 hover:text-gray-600 text-xs ml-1" data-testid={`edit-btn-${m.id}`}>Edit</button>}{can(user, "deleteMachines") && <button onClick={() => deleteMachine(m.id)} className="text-red-300 hover:text-red-500 text-xs ml-1" data-testid={`delete-btn-${m.id}`}>Del</button>}
                        {can(user, "transferMachines") && <button onClick={() => { setTransferMachine(m); setTransferTarget(""); }} className="text-purple-400 hover:text-purple-600 text-xs ml-1" data-testid={`transfer-btn-${m.id}`}>Transfer</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
